import { ErrorHandler, Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { environment } from '../../../environments/environment';
import { FlightComm } from '../models/flight-comm.model';

@Injectable()
export class CommunicationService {

  constructor(private _http: HttpClient,
              private _errorHandler: ErrorHandler) { }

  /**
   * gets specified entity via api
   * @param {number} airportID
   * @param {number} commID
   * @return {Promise<any>}
   */
  getComm(airportID: number, commID: number): Promise<any> {
    const URL = `${environment.airportsApiUrl}/airports/${airportID}/flightComm/${commID}`;
    try {
      return this._http.get(URL).toPromise();
    } catch (e) {
      this._errorHandler.handleError(e);
      throw e;
    }
  }

  /**
   * updates specified entity via api
   * @param {number} airportID
   * @param {FlightComm} comm
   * @return {Promise<any>}
   */
  putComm(airportID: number, comm: FlightComm): Promise<any> {
    const URL = `${environment.airportsApiUrl}/airports/${airportID}/flightComm/${comm.flightCommId}`;
    try {
      const BODY = this._getCommJSON(comm);
      return this._http.put(URL, BODY).toPromise();
    } catch (e) {
      this._errorHandler.handleError(e);
      throw e;
    }
  }

  /**
   * adds specified entity via api
   * @param {number} airportID
   * @param {FlightComm} comm
   * @return {Promise<any>}
   */
  postComm(airportID: number, comm: FlightComm): Promise<any> {
    const URL = `${environment.airportsApiUrl}/airports/${airportID}/flightComm/`;
    try {
      const BODY = this._getCommJSON(comm);
      return this._http.post(URL, BODY).toPromise();
    } catch (e) {
      this._errorHandler.handleError(e);
      throw e;
    }
  }

  /**
   * deletes specified entity via api
   * @param {number} airportID
   * @param {number} commID
   * @return {Promise<any>}
   */
  deleteComm(airportID: number, commID: number): Promise<any> {
    const URL = `${environment.airportsApiUrl}/airports/${airportID}/flightComm/${commID}`;
    try {
      return this._http.delete(URL).toPromise();
    } catch (e) {
      this._errorHandler.handleError(e);
      throw e;
    }
  }

  /**
   * updates specified entity via api
   * @param {number} airportID
   * @param {FlightComm} comm
   * @return {Promise<any>}
   */
  putFrequency(airportID: number, comm: FlightComm): Promise<any> {
    const URL = `${environment.airportsApiUrl}/airports/${airportID}/frequency/${comm.frequencyId}`;
    try {
      const BODY = this._getFreqJSON(comm);
      return this._http.put(URL, BODY).toPromise();
    } catch (e) {
      this._errorHandler.handleError(e);
      throw e;
    }
  }

  /**
   * adds specified entity via api
   * @param {number} airportID
   * @param {FlightComm} comm
   * @return {Promise<any>}
   */
  postFrequency(airportID: number, comm: FlightComm): Promise<any> {
    const URL = `${environment.airportsApiUrl}/airports/${airportID}/frequency/${comm.flightCommId}`;
    try {
      const BODY = this._getFreqJSON(comm);
      return this._http.post(URL, BODY).toPromise();
    } catch (e) {
      this._errorHandler.handleError(e);
      throw e;
    }
  }

  /**
   * deletes specified entity via api
   * @param {number} airportID
   * @param {number} freqID
   * @return {Promise<any>}
   */
  deleteFrequency(airportID: number, freqID: number): Promise<any> {
    const URL = `${environment.airportsApiUrl}/airports/${airportID}/frequency/${freqID}`;
    try {
      return this._http.delete(URL).toPromise();
    } catch (e) {
      this._errorHandler.handleError(e);
      throw e;
    }
  }

  /**
   * converts entity to json obj
   * @param {FlightComm} comm
   * @return {any}
   * @private
   */
  _getCommJSON (comm: FlightComm): any {
    return {
      service: comm.service,
      shortName: comm.shortName,
      radioCall: comm.radioCall,
      flightCommId: comm.flightCommId
    };
  }

  /**
   * converts entity to json obj
   * @param {FlightComm} comm
   * @return {any}
   * @private
   */
  _getFreqJSON (comm: FlightComm): any {
    return {
      freqMode: FlightComm.formatFrequencyMode(comm.freqMode),
      frequency: comm.frequency,
      sector: comm.sector === '' ? null : comm.sector,
      phoneNumber: comm.phoneNumber,
      phoneType: comm.phoneType
    };
  }
}
